<template>
  <div>
    <label
      v-if="(label || hasLabel) && !floatingLabel"
      :for="id || internalId"
      :class="
        cn('ui-text-xs !font-medium mr-1.5 uppercase text-white', labelClass)
      "
    >
      {{ label }}
    </label>
    <div class="relative">
      <select
        :id="id || internalId"
        v-model="internalValue"
        :name="name"
        :class="
          cn(
            'peer h-11 w-full cursor-pointer rounded-none border bg-white px-3 leading-[normal] text-primary block',
            floatingLabel && internalValue ? 'pb-1 pt-5' : 'py-3',
            Boolean(errorMessage)
              ? 'border-red outline outline-1 outline-red'
              : 'border-primary',
            selectClass,
          )
        "
      >
        <option
          v-if="placeholder"
          value=""
          :selected="!internalValue"
          :disabled="!unselectable"
        >
          {{ placeholder }}
        </option>
        <template
          v-for="(option, idx) in items"
          :key="valueKey ? option[valueKey] : idx"
        >
          <option
            v-if="titleKey && valueKey"
            :value="option[valueKey]"
            :selected="internalValue === option[valueKey]"
            :disabled="option.disabled"
          >
            <slot name="option" :option="option">{{ option[titleKey] }}</slot>
          </option>
        </template>
      </select>
      <label
        v-if="floatingLabel && (label || hasLabel)"
        :for="id || internalId"
        :class="[
          internalValue ? 'floating-label' : '',
          cn(
            'pointer-events-none absolute left-3 top-0 h-full w-full origin-[0_0] items-center truncate pt-4 pb-3 pr-10 ui-text-sm leading-3 text-black-60 transition duration-100 ease-in-out',
          ),
        ]"
      >
        <slot name="label">
          <span>{{ label }}</span>
        </slot>
      </label>
    </div>
    <SharedFieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || hint"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { useField } from "vee-validate";
import type { HTMLAttributes } from "vue";

const props = withDefaults(
  defineProps<{
    id?: string;
    modelValue: any;
    items: T[];
    titleKey?: string;
    valueKey?: string;
    placeholder?: string;
    name: string;
    hideDetails?: boolean;
    hint?: string;
    lazy?: boolean;
    unselectable?: boolean;
    floatingLabel?: boolean;
    label?: string;
    labelClass?: string;
    selectClass?: HTMLAttributes["class"];
  }>(),
  {
    titleKey: "label",
    valueKey: "value",
    lazy: true,
    labelClass: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", selectedValue: string): void;
}>();

const slots = useSlots();

const internalId = useId();

const { errorMessage, handleChange, meta } = useField(props.name, undefined, {
  initialValue: props.modelValue,
});

const hasLabel = computed(() => {
  return !!slots.label;
});

const internalValue = computed({
  get() {
    return props.modelValue || "";
  },
  set(newValue) {
    if (props.name) {
      // handleChange(props.modelValue);
      handleChange(newValue);
    }
    emit("update:modelValue", newValue);
  },
});

defineExpose({
  meta,
  value: props.modelValue,
  name: props.name,
});
</script>

<style>
.floating-label {
  @apply -translate-y-1 scale-70 peer-disabled:pointer-events-none peer-disabled:opacity-50 text-primary;
}

select,
option.selected {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
